import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { DebounceInput } from "react-debounce-input";
import { withAlert } from "react-alert";
import { withTranslation } from "react-i18next";
import isClassDiaryDemo from "../../demo/isClassDiaryDemo";
import {
  buildDemoStudentDirectory,
  filterDemoStudentDirectory
} from "../../demo/buildDemoStudentDirectory";
import { LIST_PAGE_SIZE } from "../../constants/listPagination";
import StudentService from "../../services/StudentService";
import TeacherService from "../../services/TeacherService";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import TableGrid from "../../waybee-ui/TableGrid";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import { Can } from "../../acl/can";
import StudentClassStatus from "../StudentProfile/components/StudentClassStatus";

const LoadContainer = styled.div`
  text-align: center;
  margin: 8px;
`;

function StudentRowClassStatus({ classStatus }) {
  const theme = useTheme();
  const showClassStatus = useMediaQuery(theme.breakpoints.up("md"));

  if (!showClassStatus) {
    return null;
  }

  return (
    <TableGrid.Text md={5}>
      <StudentClassStatus classStatus={classStatus} compact />
    </TableGrid.Text>
  );
}

StudentRowClassStatus.propTypes = {
  classStatus: PropTypes.object
};

StudentRowClassStatus.defaultProps = {
  classStatus: null
};

class Students extends Component {
  constructor(props) {
    super(props);
    this.loadSeq = 0;
    this.state = {
      filter: { search: "", classId: "" },
      classes: [],
      students: [],
      skip: 0,
      total: 0,
      loading: true,
      loadingClasses: false
    };
  }

  componentDidMount() {
    this.loadClasses();
    this.loadStudents(true);
    window.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    const container = document.documentElement;
    if (
      container.scrollTop + window.innerHeight >=
      container.offsetHeight - 2
    ) {
      this.loadMoreStudents();
    }
  };

  loadClasses = async () => {
    this.setState({ loadingClasses: true });
    try {
      if (isClassDiaryDemo()) {
        const all = buildDemoStudentDirectory();
        const byId = new Map();
        all.forEach(student => {
          if (!student.classId || byId.has(student.classId)) return;
          byId.set(student.classId, {
            id: student.classId,
            name: student.turmaLabel || student.className || student.classId
          });
        });
        this.setState({ classes: Array.from(byId.values()) });
        return;
      }
      const res = await TeacherService.getClasses();
      const items = Array.isArray(res) ? res : (res && res.items) || [];
      this.setState({
        classes: items.map(classe => ({
          id: classe.id,
          name: classe.name || classe.id
        }))
      });
    } catch (e) {
      const { alert, t } = this.props;
      this.setState({ classes: [] });
      alert.show(t("students.errorFetchClasses"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingClasses: false });
    }
  };

  loadStudents = async (reset = false) => {
    const { loading, skip, filter } = this.state;
    if (loading && !reset) return;

    this.loadSeq += 1;
    const seq = this.loadSeq;
    const currentSkip = reset ? 0 : skip;
    const search = filter.search || "";
    const classId = filter.classId || "";

    this.setState(
      reset ? { loading: true, students: [], skip: 0 } : { loading: true }
    );

    try {
      let items = [];
      let total = 0;

      if (isClassDiaryDemo()) {
        const full = filterDemoStudentDirectory(search, classId);
        total = full.length;
        items = full.slice(currentSkip, currentSkip + LIST_PAGE_SIZE);
      } else {
        const res = await StudentService.getStudents({
          search,
          classId,
          limit: LIST_PAGE_SIZE,
          skip: currentSkip
        });
        items = (res && res.items) || (Array.isArray(res) ? res : []);
        total =
          res && typeof res.total === "number"
            ? res.total
            : Number(res && res.total) || items.length;
      }

      if (seq !== this.loadSeq) return;

      this.setState(prev => ({
        students: reset ? items : prev.students.concat(items),
        total,
        skip: currentSkip + items.length,
        loading: false
      }));
    } catch (e) {
      if (seq !== this.loadSeq) return;
      const { alert, t } = this.props;
      this.setState(prev => ({
        students: reset ? [] : prev.students,
        loading: false
      }));
      alert.show(t("students.errorFetch"), {
        title: t("common.actions.error")
      });
    }
  };

  loadMoreStudents = () => {
    const { students, total, loading } = this.state;
    if (loading || students.length >= total) return;
    this.loadStudents(false);
  };

  setSearch = value => {
    this.setState(
      prev => ({ filter: { ...prev.filter, search: value }, skip: 0 }),
      () => this.loadStudents(true)
    );
  };

  setClassFilter = classe => {
    this.setState(
      prev => ({
        filter: {
          ...prev.filter,
          classId: classe && classe.id ? classe.id : ""
        },
        skip: 0
      }),
      () => this.loadStudents(true)
    );
  };

  goToProfile = student => {
    const { history } = this.props;
    const ra = student.ra || student.id;
    if (student.classId) {
      localStorage.setItem("class", student.classId);
    }
    history.push({
      pathname: `/student/${ra}`,
      state: { from: "/students" }
    });
  };

  renderStudentList = () => {
    const { students, loading } = this.state;
    const { t } = this.props;

    if (loading && students.length === 0) {
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );
    }

    if (!loading && students.length === 0) {
      return (
        <Box py={4} textAlign="center" color="textSecondary">
          {t("students.empty")}
        </Box>
      );
    }

    return (
      <>
        <TableGrid>
          {students.map(student => (
            <Can
              key={student.ra || student.id}
              I="use module"
              a="diarySpecificStudent"
              passThrough
            >
              {allowed => (
                <TableGrid.Row
                  photo={student.photo}
                  photoName={student.name}
                  actionIcon={allowed}
                  button={allowed}
                  onClick={
                    allowed ? () => this.goToProfile(student) : undefined
                  }
                >
                  <TableGrid.Title xs={12} sm={6} md={3}>
                    {student.name}
                  </TableGrid.Title>
                  <TableGrid.Text xs={6} sm={3} md={2}>
                    {t("students.raLabel", { ra: student.ra })}
                  </TableGrid.Text>
                  <TableGrid.Text xs={6} sm={3} md={2}>
                    {t("students.classLabel", {
                      label: student.turmaLabel || student.classId || "—"
                    })}
                  </TableGrid.Text>
                  <StudentRowClassStatus classStatus={student.classStatus} />
                </TableGrid.Row>
              )}
            </Can>
          ))}
        </TableGrid>
        {loading && students.length > 0 ? (
          <LoadContainer>
            <CircularProgress size={24} />
          </LoadContainer>
        ) : null}
      </>
    );
  };

  render() {
    const { filter, classes, loadingClasses } = this.state;
    const { t } = this.props;
    const selectedClass =
      classes.find(classe => classe.id === filter.classId) || null;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Group>
            <Box>
              <Heading level={1} icon="search" gutterBottom>
                {t("students.title")}
              </Heading>
              <Box pb={4} pt={2}>
                <form noValidate autoComplete="off">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <SearchableSelect
                        label={t("common.labels.class")}
                        options={classes}
                        value={selectedClass}
                        disabled={loadingClasses}
                        onChange={this.setClassFilter}
                        getOptionLabel={option =>
                          option.name || option.id || ""}
                        getOptionSelected={(option, selected) =>
                          option.id === selected.id}
                        noOptionsText={t("common.messages.noClass")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DebounceInput
                        minLength={0}
                        debounceTimeout={300}
                        element={TextField}
                        value={filter.search}
                        onChange={e => this.setSearch(e.target.value)}
                        label={t("students.search")}
                        placeholder={t("students.searchPlaceholder")}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </Box>
            <Box>{this.renderStudentList()}</Box>
          </Group>
        </Grid>
      </Grid>
    );
  }
}

Students.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  alert: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(Students)));
